// src/components/inventory/BarcodeScanner.tsx
// Lettura del codice a barre dalla fotocamera, pensata per il telefono.
// Dove c'è il BarcodeDetector nativo (Chrome su Android) si usa quello;
// dove non c'è (Safari su iPhone, Firefox) si scarica ZXing al primo avvio
// e si decodifica in JavaScript, così la scansione funziona ovunque.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, Loader2, ScanBarcode, Flashlight, AlertTriangle, X } from 'lucide-react';

interface BarcodeScannerProps {
    onDetected: (code: string) => void;
    /** Sospende la lettura senza dimenticare che l'utente l'aveva avviata (es. modale aperto sopra). */
    paused?: boolean;
}

type ScannerStatus = 'off' | 'starting' | 'on';

/** I codici dei prodotti sono lineari; il QR c'è solo perché non costa nulla. */
const NATIVE_FORMATS = [
    'ean_13',
    'ean_8',
    'upc_a',
    'upc_e',
    'code_128',
    'code_39',
    'itf',
    'codabar',
    'qr_code',
];

const CONSTRAINTS: MediaStreamConstraints = {
    audio: false,
    video: {
        facingMode: { ideal: 'environment' },
        width: { ideal: 1280 },
        height: { ideal: 720 },
    },
};

type Translate = (key: string, fallback: string) => string;

const describeError = (err: unknown, t: Translate): string => {
    const name = (err as DOMException)?.name;
    if (typeof window !== 'undefined' && window.isSecureContext === false) {
        return t('inventory.scan.err_insecure', 'La fotocamera funziona solo su una connessione sicura (https).');
    }
    if (name === 'NotAllowedError' || name === 'SecurityError') {
        return t(
            'inventory.scan.err_denied',
            'Permesso fotocamera negato: autorizzalo nelle impostazioni del browser e riprova.'
        );
    }
    if (name === 'NotFoundError' || name === 'OverconstrainedError') {
        return t('inventory.scan.err_no_camera', 'Nessuna fotocamera disponibile su questo dispositivo.');
    }
    if (name === 'NotReadableError' || name === 'AbortError') {
        return t('inventory.scan.err_busy', "La fotocamera è occupata da un'altra app: chiudila e riprova.");
    }
    return t(
        'inventory.scan.err_generic',
        'Non riesco ad avviare la fotocamera: inserisci il codice a mano qui sotto.'
    );
};

/** Ritorna i formati leggibili nativamente, o null se conviene passare a ZXing. */
const getNativeFormats = async (): Promise<string[] | null> => {
    const Detector = (window as any).BarcodeDetector;
    if (typeof Detector?.getSupportedFormats !== 'function') return null;
    try {
        const supported: string[] = await Detector.getSupportedFormats();
        const usable = NATIVE_FORMATS.filter((f) => supported.includes(f));
        // Se il browser sa leggere solo i QR non ci serve: i prodotti hanno codici lineari.
        return usable.some((f) => f !== 'qr_code') ? usable : null;
    } catch {
        return null;
    }
};

const BarcodeScanner: React.FC<BarcodeScannerProps> = ({ onDetected, paused = false }) => {
    const { t } = useTranslation();

    const videoRef = useRef<HTMLVideoElement | null>(null);
    /** Come fermare la lettura in corso (loop nativo o controlli ZXing). */
    const cleanupRef = useRef<(() => void) | null>(null);
    // Via ref, così un cambio di identità di `onDetected`/`t` non fa ripartire la fotocamera.
    const onDetectedRef = useRef(onDetected);
    const tRef = useRef(t);
    useEffect(() => {
        onDetectedRef.current = onDetected;
        tRef.current = t;
    }, [onDetected, t]);

    const [wanted, setWanted] = useState(false);
    const [status, setStatus] = useState<ScannerStatus>('off');
    const [error, setError] = useState<string | null>(null);
    const [torch, setTorch] = useState({ available: false, on: false });

    const getTrack = () =>
        ((videoRef.current?.srcObject as MediaStream | null) ?? null)?.getVideoTracks()[0] ?? null;

    const teardown = useCallback(() => {
        cleanupRef.current?.();
        cleanupRef.current = null;
        const video = videoRef.current;
        const stream = (video?.srcObject as MediaStream | null) ?? null;
        stream?.getTracks().forEach((track) => track.stop());
        if (video) video.srcObject = null;
        setStatus('off');
        setTorch((prev) => (prev.available || prev.on ? { available: false, on: false } : prev));
    }, []);

    useEffect(() => {
        if (!wanted || paused) {
            teardown();
            return;
        }

        let cancelled = false;
        setError(null);
        setStatus('starting');

        const hit = (raw: string) => {
            const code = raw.trim();
            if (!code || cancelled) return;
            navigator.vibrate?.(60);
            onDetectedRef.current(code);
        };

        /** Chrome/Android: il browser decodifica da sé, basta dargli i frame. */
        const startNative = async (video: HTMLVideoElement, formats: string[]) => {
            const stream = await navigator.mediaDevices.getUserMedia(CONSTRAINTS);
            if (cancelled) {
                stream.getTracks().forEach((track) => track.stop());
                return;
            }
            video.srcObject = stream;
            // Su iPhone senza questi il video parte a schermo intero o non parte affatto.
            video.setAttribute('playsinline', 'true');
            video.setAttribute('muted', 'true');
            video.muted = true;
            await video.play().catch(() => undefined);

            const detector = new (window as any).BarcodeDetector({ formats });
            let timer: number | null = null;
            let stopped = false;

            const tick = async () => {
                if (stopped) return;
                try {
                    const codes = await detector.detect(video);
                    const raw = codes?.[0]?.rawValue;
                    if (raw) {
                        hit(raw);
                        return;
                    }
                } catch {
                    /* frame illeggibile: si riprova al giro dopo */
                }
                timer = window.setTimeout(tick, 200);
            };

            cleanupRef.current = () => {
                stopped = true;
                if (timer) clearTimeout(timer);
            };
            tick();
        };

        /** Safari/Firefox: ZXing arriva solo qui, in un chunk a parte. */
        const startZxing = async (video: HTMLVideoElement) => {
            const [{ BrowserMultiFormatReader }, { BarcodeFormat, DecodeHintType }] = await Promise.all([
                import('@zxing/browser'),
                import('@zxing/library'),
            ]);
            if (cancelled) return;

            const hints = new Map<number, unknown>();
            hints.set(DecodeHintType.POSSIBLE_FORMATS, [
                BarcodeFormat.EAN_13,
                BarcodeFormat.EAN_8,
                BarcodeFormat.UPC_A,
                BarcodeFormat.UPC_E,
                BarcodeFormat.UPC_EAN_EXTENSION,
                BarcodeFormat.CODE_128,
                BarcodeFormat.CODE_39,
                BarcodeFormat.ITF,
                BarcodeFormat.CODABAR,
                BarcodeFormat.QR_CODE,
            ]);
            hints.set(DecodeHintType.TRY_HARDER, true);

            const reader = new BrowserMultiFormatReader(hints as any, {
                delayBetweenScanAttempts: 100,
                delayBetweenScanSuccess: 800,
            });

            const controls = await reader.decodeFromConstraints(CONSTRAINTS, video, (result) => {
                if (result) hit(result.getText());
            });

            if (cancelled) {
                controls.stop();
                return;
            }
            cleanupRef.current = () => controls.stop();
        };

        (async () => {
            try {
                const video = videoRef.current;
                if (!video) return;
                if (!navigator.mediaDevices?.getUserMedia) {
                    throw new DOMException('getUserMedia non disponibile', 'NotSupportedError');
                }

                const nativeFormats = await getNativeFormats();
                if (cancelled) return;

                if (nativeFormats) await startNative(video, nativeFormats);
                else await startZxing(video);

                if (cancelled) return;
                setStatus('on');

                const capabilities = getTrack()?.getCapabilities?.() as
                    | (MediaTrackCapabilities & { torch?: boolean })
                    | undefined;
                setTorch({ available: !!capabilities?.torch, on: false });
            } catch (err) {
                if (cancelled) return;
                console.error('Barcode scanner error', err);
                setError(describeError(err, tRef.current as Translate));
                setStatus('off');
                setWanted(false);
            }
        })();

        return () => {
            cancelled = true;
            teardown();
        };
    }, [wanted, paused, teardown]);

    const toggleTorch = async () => {
        const track = getTrack();
        if (!track) return;
        const next = !torch.on;
        try {
            await track.applyConstraints({ advanced: [{ torch: next }] } as any);
            setTorch((prev) => ({ ...prev, on: next }));
        } catch {
            setTorch((prev) => ({ ...prev, available: false }));
        }
    };

    const isBusy = status === 'starting';

    return (
        <div className="space-y-3">
            <div className="relative rounded-2xl bg-gray-900 overflow-hidden aspect-[4/3]">
                {/* Il video resta sempre montato e dimensionato: ZXing legge da qui. */}
                <video
                    ref={videoRef}
                    playsInline
                    muted
                    autoPlay
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity ${status === 'on' ? 'opacity-100' : 'opacity-0'
                        }`}
                />

                {status !== 'on' && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-gray-400">
                        {isBusy ? (
                            <Loader2 size={28} className="animate-spin" />
                        ) : (
                            <ScanBarcode size={32} className="opacity-60" />
                        )}
                        <p className="text-sm">
                            {isBusy
                                ? t('inventory.scan.starting', 'Avvio fotocamera...')
                                : t('inventory.scan.frame', 'Inquadra il codice a barre')}
                        </p>
                    </div>
                )}

                {status === 'on' && (
                    <>
                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                            <div className="w-4/5 h-1/3 rounded-2xl border-2 border-white/70" />
                        </div>
                        {torch.available && (
                            <button
                                type="button"
                                onClick={toggleTorch}
                                aria-label={t('inventory.scan.torch', 'Torcia') as string}
                                className={`absolute top-3 right-3 p-2.5 rounded-full backdrop-blur-sm transition-colors ${torch.on ? 'bg-white text-gray-900' : 'bg-black/50 text-white hover:bg-black/70'
                                    }`}
                            >
                                <Flashlight size={18} />
                            </button>
                        )}
                    </>
                )}
            </div>

            <div className="flex justify-center">
                <button
                    type="button"
                    onClick={() => setWanted((prev) => !prev)}
                    disabled={isBusy}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold shadow-sm transition-colors disabled:opacity-60 ${wanted
                        ? 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                        : 'bg-clip-500 hover:bg-clip-600 text-white'
                        }`}
                >
                    {isBusy ? <Loader2 size={16} className="animate-spin" /> : wanted ? <X size={16} /> : <Camera size={16} />}
                    {wanted
                        ? t('inventory.scan.stop', 'Ferma scansione')
                        : t('inventory.scan.start', 'Avvia scansione')}
                </button>
            </div>

            {error && (
                <div className="flex items-start gap-2.5 p-3 rounded-xl bg-amber-50 border border-amber-100 text-amber-700">
                    <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                    <p className="text-sm font-medium">{error}</p>
                </div>
            )}
        </div>
    );
};

export default BarcodeScanner;
